import { Link, matchPath, useLocation } from "react-router-dom";
import { SidebarMenuSub, SidebarMenuSubItem } from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { useWorkspace } from "@/features/Workspaces/WorkspaceContext";
import {
  buildWorkspaceAdminPath,
  buildWorkspaceDashboardPath,
  normalizeWorkspaceGroupForPath,
  parseWorkspaceContextFromPathname,
} from "@/features/Workspaces/workspaceRoutes";
import { normalizeCode } from "@/features/Workspaces/deriveWorkspaces";

type SubmenuProps = {
  module: "dashboard" | "admin";
  open: boolean;
  menuItem: Record<string, string>;
  isSidebarClose: boolean; // not needed for shadcn sub, you can keep/remove
  client?: string;
};

export default function Submenu({
  module,
  open,
  menuItem,
  client,
}: SubmenuProps) {
  const location = useLocation();
  const { current } = useWorkspace();
  if (!open) return null;

  const routeContext = parseWorkspaceContextFromPathname(location.pathname);
  const workspace = routeContext?.workspace ?? current?.code ?? "";
  const group = normalizeWorkspaceGroupForPath(
    routeContext?.group ?? current?.group,
  );
  const safeDecode = (value: string) => {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  };
  const normalizeComparablePath = (value: string) => {
    const [path] = safeDecode(value).split(/[?#]/, 1);
    return path
      .split("/")
      .filter(Boolean)
      .map((segment) => normalizeCode(segment))
      .join("/");
  };
  const matchesNormalizedPath = (pathnameValue: string, target: string) => {
    const currentPath = normalizeComparablePath(pathnameValue);
    const targetPath = normalizeComparablePath(target);
    return (
      currentPath === targetPath || currentPath.startsWith(`${targetPath}/`)
    );
  };

  return (
    <SidebarMenuSub className="flex flex-col gap-1 px-2">
      {Object.entries(menuItem).map(([pathItem, nameItem]) => {
        const to =
          module === "admin"
            ? buildWorkspaceAdminPath({
                group,
                workspace,
                path: `${client ?? ""}/${pathItem}`,
              })
            : buildWorkspaceDashboardPath({
                group,
                workspace,
                uuid: pathItem,
              });
        const aliasPathItem =
          module === "admin" && client && pathItem.startsWith(`${client}_`)
            ? pathItem.slice(`${client}_`.length)
            : pathItem;
        const aliasTo =
          module === "admin" && aliasPathItem !== pathItem
            ? buildWorkspaceAdminPath({
                group,
                workspace,
                path: `${client}/${aliasPathItem}`,
              })
            : to;
        const adminBase =
          module === "admin"
            ? buildWorkspaceAdminPath({
                group,
                workspace,
              })
            : "";
        const expandedTo =
          module === "admin" && client
            ? `${adminBase}/${client}/${pathItem}`
            : to;
        const expandedAliasTo =
          module === "admin" && client && aliasPathItem !== pathItem
            ? `${adminBase}/${client}/${aliasPathItem}`
            : expandedTo;
        const isActive =
          !!matchPath({ path: to, end: false }, location.pathname) ||
          !!matchPath(
            { path: `${to}/:rest/*`, end: false },
            location.pathname,
          ) ||
          !!matchPath({ path: aliasTo, end: false }, location.pathname) ||
          !!matchPath(
            { path: `${aliasTo}/:rest/*`, end: false },
            location.pathname,
          ) ||
          !!matchPath({ path: expandedTo, end: false }, location.pathname) ||
          !!matchPath(
            { path: `${expandedTo}/:rest/*`, end: false },
            location.pathname,
          ) ||
          !!matchPath(
            { path: expandedAliasTo, end: false },
            location.pathname,
          ) ||
          !!matchPath(
            { path: `${expandedAliasTo}/:rest/*`, end: false },
            location.pathname,
          ) ||
          matchesNormalizedPath(location.pathname, to) ||
          matchesNormalizedPath(location.pathname, aliasTo) ||
          matchesNormalizedPath(location.pathname, expandedTo) ||
          matchesNormalizedPath(location.pathname, expandedAliasTo);

        return (
          <SidebarMenuSubItem
            key={pathItem}
            className={cn(
              "relative overflow-visible",
              isActive
                ? "before:content-[''] before:absolute before:top-0 before:bottom-0 before:left-[-9px] before:w-0 before:border-l before:border-blue-700 before:rounded"
                : "",
            )}
          >
            <Link
              to={to}
              className={cn(
                "block w-full rounded-sm pl-3 pr-2 py-2 text-xs leading-tight whitespace-normal break-words transition-colors",
                isActive
                  ? "text-black font-medium"
                  : "text-gray-600 hover:text-gray-950 hover:bg-gray-100/60",
              )}
              data-active-subitem={isActive ? "true" : undefined}
            >
              {nameItem}
            </Link>
          </SidebarMenuSubItem>
        );
      })}
    </SidebarMenuSub>
  );
}
